import React, { useEffect, useRef, useState } from 'react';
import { Dropdown, FormControl } from 'react-bootstrap';

const maxLength = 250;
const CommentInput = ({ users, comment, onCommentChange, setTaggedUsers }) => {
  const [mentioning, setMentioning] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [activeUserIndex, setActiveUserIndex] = useState(-1);
  const [mentionStart, setMentionStart] = useState(null);
  const [cursorPosition, setCursorPosition] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [length, setLength] = useState(comment.length);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const userMap = new Map(users.map((user) => [user?.full_name?.trim()?.toLowerCase(), user?.id]));

  useEffect(() => {
    if (cursorPosition !== null && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
    }
  }, [cursorPosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setMentioning(false);
        setFilteredUsers([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!inputRef.current || !mentioning) return;

    const { offsetTop, offsetLeft, clientHeight, clientWidth, scrollLeft } = inputRef.current;
    const textBeforeCursor = comment.slice(0, cursorPosition);
    const cursorLineIndex = textBeforeCursor.split('\n').length - 1;
    const lineHeight = clientHeight / Math.max(textBeforeCursor.split('\n').length, 1);
    const dropdownTop = offsetTop + lineHeight * cursorLineIndex + 30;

    const charWidth = 8;
    const dropdownLeft =
      offsetLeft +
      (cursorPosition - textBeforeCursor.lastIndexOf('\n', cursorPosition) - 1) * charWidth -
      scrollLeft;

    const dropdownWidth = 150;
    const maxLeft = Math.max(0, Math.min(clientWidth - dropdownWidth, dropdownLeft));

    setDropdownPosition({ top: dropdownTop, left: maxLeft });
  }, [mentioning, cursorPosition, comment]);

  useEffect(() => {
    if (filteredUsers.length > 0) {
      setActiveUserIndex(0);
    }
  }, [filteredUsers]);

  const findMentionBounds = (start) => {
    let end = start;
    while (end < comment.length && !/\s|\n/.test(comment[end])) {
      end++;
    }
    let mentionStart = start;
    while (
      mentionStart > 0 &&
      !/\s|\n/.test(comment[mentionStart - 1]) &&
      comment[mentionStart - 1] !== '@'
    ) {
      mentionStart--;
    }

    return { start: mentionStart, end };
  };

  const getAccountId = (displayName) => userMap.get(displayName.toLowerCase());

  const updateTaggedUsers = (updatedComment) => {
    const currentMentions = new Set();
    const mentionRegex = /@\[([\w'_.-]+)\]|@([\w'_.-]+(?:\s+[\w'_.-]+)?)/gi;
    let match;

    while ((match = mentionRegex.exec(updatedComment)) !== null) {
      if (match[1]) {
        const accountId = match[1].trim();
        currentMentions.add(accountId);
      } else if (match[2]) {
        const displayName = match[2].trim().replace(/_/g, ' ');
        const accountId = getAccountId(displayName);
        if (accountId) {
          currentMentions.add(accountId);
        }
      }
    }
    const currentMentionArray = [...currentMentions];
    setTaggedUsers(currentMentionArray);
  };

  const handleChange = (e) => {
    let value = e.target.value;
    const cursorPos = e.target.selectionStart;
    if (value.length > maxLength) {
      value = value.slice(0, maxLength);
    }
    const newLength = value.length;
    onCommentChange(value);
    setCursorPosition(cursorPos);

    const mentionIndex = value.lastIndexOf('@', cursorPos - 1);

    if (mentionIndex > -1) {
      setMentionStart(mentionIndex);

      const mentionText = value.slice(mentionIndex + 1, cursorPos).trim();

      if (mentionText) {
        const filtered = users?.filter((user) =>
          user?.full_name.trim().toLowerCase().startsWith(mentionText.toLowerCase())
        );
        setFilteredUsers(filtered);
        setMentioning(filtered.length > 0);
      } else {
        setFilteredUsers(users);
        setMentioning(true);
      }
    } else {
      setMentioning(false);
      setFilteredUsers([]);
    }
    setLength(newLength);
    updateTaggedUsers(value);
  };

  useEffect(() => {
    setLength(comment.length);
  }, [comment]);

  const handleKeyDown = (e) => {
    if (mentioning) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveUserIndex((prevIndex) =>
          prevIndex < filteredUsers?.length - 1 ? prevIndex + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveUserIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : filteredUsers.length - 1
        );
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (activeUserIndex >= 0) {
          handleUserSelect(filteredUsers[activeUserIndex]);
        }
      } else if (e.key === 'Backspace') {
        if (mentionStart !== null) {
          const { start, end } = findMentionBounds(mentionStart);
          const cursorPos = e.target.selectionStart;
          if (cursorPos >= start && cursorPos <= end) {
            const newComment = comment.slice(0, start) + comment.slice(end);
            onCommentChange(newComment);
            updateTaggedUsers(newComment);

            setMentioning(false);
            setActiveUserIndex(-1);
            setCursorPosition(start);
          }
        } else {
          setMentioning(false);
        }
      }
    }
  };

  const handleUserSelect = (user) => {
    const { start } = findMentionBounds(mentionStart);
    const end = findMentionBounds(start).end;
    const username = user?.full_name.trim();
    const isAlreadyTagged = comment.includes(`@${username}`);
    if (isAlreadyTagged) {
      return;
    }
    const newComment =
      comment.substring(0, start) +
      `@${username}` +
      comment.substring(end);
    onCommentChange(newComment);
    updateTaggedUsers(newComment);
    setMentioning(false);
    setActiveUserIndex(-1);
    setCursorPosition(start + username.length + 2);
  };

  return (
    <div style={{ position: 'relative' }}>
      <FormControl
        ref={inputRef}
        as="textarea"
        rows={3}
        maxLength={maxLength}
        value={comment}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder="Write a comment..."
      />
      <small
        className={`float-end mt-0 ${length >= maxLength - 15 ? 'text-danger' : 'text-muted'}`}>
        {length}/{maxLength}
      </small>
      {mentioning && filteredUsers.length > 0 && (
        <Dropdown.Menu
          ref={dropdownRef}
          show
          style={{
            position: 'absolute',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: '150px',
            zIndex: 1000,
          }}>
          {filteredUsers?.map((user, index) => (
            <Dropdown.Item
              key={user?.id}
              style={{ fontSize: '0.8rem' }}
              onClick={() => handleUserSelect(user)}
              active={index === activeUserIndex}>
              {user?.full_name.trim()}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      )}
    </div>
  );
};

export default CommentInput;
